import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DisplayPreferencesProvider } from "./context/DisplayPreferencesContext";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import KeyboardShortcutsModal from "./components/common/KeyboardShortcutsModal";
import EasterEgg from "./components/common/EasterEgg";
import ErrorBoundary from "./components/common/ErrorBoundary";

// Layout - Keep eagerly loaded as it's always needed
import MainLayout from "./components/common/EnhancedMainLayout";
import ThemeManager from "./components/common/ThemeManager";

// Lazy-loaded Pages
const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("./pages/auth/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/dashboard/DashboardPage"));
const AccountsPage = lazy(() => import("./pages/accounting/AccountsPage"));
const AccountDetailPage = lazy(() => import("./pages/accounting/AccountDetailPage"));
const TemplatesPage = lazy(() => import("./pages/accounting/TemplatesPage"));
const ImportPage = lazy(() => import("./pages/accounting/ImportPage"));
const ReconciliationsPage = lazy(() => import("./pages/accounting/ReconciliationsPage"));
const ReconciliationDetailPage = lazy(() => import("./pages/accounting/ReconciliationDetailPage"));
const GeneralLedgerPage = lazy(() => import("./pages/accounting/GeneralLedgerPage"));
const TrialBalancePage = lazy(() => import("./pages/accounting/TrialBalancePage"));
const WorkPapersPage = lazy(() => import("./pages/audit/WorkPapersPage"));
const UserManagementPage = lazy(() => import("./pages/settings/UserManagementPage"));
const AuditTrailPage = lazy(() => import("./pages/audit/AuditTrailPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const SearchPage = lazy(() => import("./pages/dashboard/SearchPage"));
const AnalyticsPage = lazy(() => import("./pages/analytics/AnalyticsPage"));
const FAQPage = lazy(() => import("./pages/settings/FAQPage"));
const MDAPage = lazy(() => import("./pages/reporting/MDAPage"));
const FPAPage = lazy(() => import("./pages/fpa/FPAPage"));
const FinancialAnalyticsDashboard = lazy(() => import("./pages/analytics/FinancialAnalyticsDashboard"));
const CloudStoragePage = lazy(() => import("./pages/integrations/CloudStoragePage"));
const PredictiveAnalyticsPage = lazy(() => import("./pages/analytics/PredictiveAnalyticsPage"));
const AutomatedReportsPage = lazy(() => import("./pages/reporting/AutomatedReportsPage"));
const BalanceSheetPage = lazy(() => import("./pages/financial-statements/BalanceSheetPage"));
const IncomeStatementPage = lazy(() => import("./pages/financial-statements/IncomeStatementPage"));
const CashFlowStatementPage = lazy(() => import("./pages/financial-statements/CashFlowStatementPage"));
const OperationalDashboard = lazy(() => import("./pages/analytics/OperationalDashboard"));
const AutomatedReconciliationPage = lazy(() => import("./pages/automation/AutomatedReconciliationPage"));
const CrossEntityReportingPage = lazy(() => import("./pages/reporting/CrossEntityReportingPage"));
const PermissionsManagementPage = lazy(() => import("./pages/settings/PermissionsManagementPage"));
const MFASettingsPage = lazy(() => import("./pages/settings/MFASettingsPage"));
const MFAEnforcementPage = lazy(() => import("./pages/settings/MFAEnforcementPage"));
const FPAAnalysisPage = lazy(() => import("./pages/analytics/FPAAnalysisPage"));
const AdHocAnalysisPage = lazy(() => import("./pages/analytics/AdHocAnalysisPage"));
const DataImportPage = lazy(() => import("./pages/accounting/DataImportPage"));
const FiscalYearSettingsPage = lazy(() => import("./pages/settings/FiscalYearSettingsPage"));
const PeriodCloseManagementPage = lazy(() => import("./pages/accounting/PeriodCloseManagementPage"));
const TrialBalanceReportPage = lazy(() => import("./pages/accounting/TrialBalanceReportPage"));
const AuditWorkPapersPage = lazy(() => import("./pages/audit/AuditWorkPapersPage"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  useKeyboardShortcuts(); // Add keyboard shortcuts
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <DisplayPreferencesProvider>
          <ThemeManager />
          <EasterEgg />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
                
                <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="accounts" element={<AccountsPage />} />
                  <Route path="accounts/:accountId" element={<AccountDetailPage />} />
                  <Route path="accounts/general-ledger" element={<GeneralLedgerPage />} />
                  <Route path="general-ledger" element={<GeneralLedgerPage />} />
                  <Route path="accounts/trial-balance" element={<TrialBalancePage />} />
                  <Route path="trial-balance" element={<TrialBalancePage />} />
                  <Route path="trial-balance-report" element={<TrialBalanceReportPage />} />
                  <Route path="period-close" element={<PeriodCloseManagementPage />} />
                  <Route path="balance-sheet" element={<BalanceSheetPage />} />
                  <Route path="income-statement" element={<IncomeStatementPage />} />
                  {/* Financial Statements nested routes */}
                  <Route path="financial-statements">
                    <Route path="balance-sheet" element={<BalanceSheetPage />} />
                    <Route path="income-statement" element={<IncomeStatementPage />} />
                    <Route path="cash-flow-statement" element={<CashFlowStatementPage />} />
                  </Route>
                  <Route path="templates" element={<TemplatesPage />} />
                  <Route path="import" element={<ImportPage />} />
                  <Route path="data-import" element={<DataImportPage />} />
                  <Route path="cloud-storage" element={<CloudStoragePage />} />
                  <Route path="reconciliations" element={<ReconciliationsPage />} />
                  <Route path="reconciliations/:reconId" element={<ReconciliationDetailPage />} />
                  <Route path="workpapers" element={<WorkPapersPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="operational-dashboard" element={<OperationalDashboard />} />
                  <Route path="financial-analytics" element={<FinancialAnalyticsDashboard />} />
                  <Route path="fpa-analysis" element={<FPAAnalysisPage />} />
                  <Route path="adhoc-analysis" element={<AdHocAnalysisPage />} />
                  <Route path="predictive-analytics" element={<PredictiveAnalyticsPage />} />
                  <Route path="automated-reports" element={<AutomatedReportsPage />} />
                  <Route path="automated-reconciliation" element={<AutomatedReconciliationPage />} />
                  <Route path="cross-entity-reporting" element={<CrossEntityReportingPage />} />
                  <Route path="permissions" element={<AdminRoute><PermissionsManagementPage /></AdminRoute>} />
                  <Route path="mfa-settings" element={<MFASettingsPage />} />
                  <Route path="mfa-enforcement" element={<AdminRoute><MFAEnforcementPage /></AdminRoute>} />
                  <Route path="fiscal-year-settings" element={<AdminRoute><FiscalYearSettingsPage /></AdminRoute>} />
                  <Route path="audit-workpapers" element={<AuditWorkPapersPage />} />
                  <Route path="mda" element={<MDAPage />} />
                  <Route path="fpa" element={<FPAPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="faq" element={<FAQPage />} />
                  <Route path="search" element={<SearchPage />} />
                  <Route path="users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
                  <Route path="audit-trail" element={<AdminRoute><AuditTrailPage /></AdminRoute>} />
                </Route>
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
          <KeyboardShortcutsModal />
        </DisplayPreferencesProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
